import { type Stripe } from "stripe";

import { type Database } from "@fitness-app/supabase";

import { type LastNameFieldValues } from "./Automation";
import { type RadixColor } from "./CheckoutVariant";
import { type ClientInvoiceData } from "./Order";
import { type CheckoutSettings } from "./ProductsSettings";

export type ProductEntity = Database["public"]["Tables"]["product"]["Row"];
export type ProductChargeTypeEnum = Database["public"]["Enums"]["ProductChargeType"];
export type PaymentClientEnum = Database["public"]["Enums"]["PaymentClient"];
export type ProductTypeEnum = Database["public"]["Enums"]["ProductType"];
export type ProductStatusEnum = Database["public"]["Enums"]["ProductStatus"];

export type PricePaymentType = "one_time" | "recurring";
export type OrderPaymentType = "one_time" | "recurring" | "free";

export const PaymentClient: Record<
  Database["public"]["Enums"]["PaymentClient"],
  Database["public"]["Enums"]["PaymentClient"]
> = {
  STRIPE: "STRIPE",
  PAY_NOW: "PAY_NOW",
  TPAY: "TPAY",
  PAY_PO: "PAY_PO",
  MANUAL: "MANUAL",
} as const;

export const ProductChargeType: Record<
  Database["public"]["Enums"]["ProductChargeType"],
  Database["public"]["Enums"]["ProductChargeType"]
> = {
  FREE: "FREE",
  PAID: "PAID",
} as const;

export const ProductStatus: Record<
  Database["public"]["Enums"]["ProductStatus"],
  Database["public"]["Enums"]["ProductStatus"]
> = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  ARCHIVED: "ARCHIVED",
} as const;

export const PaymentTypeOption = {
  OneTime: "one_time",
  Recurring: "recurring",
  Free: "free",
} as const;

export const OrderPaymentTypeOption = {
  OneTime: "one_time",
  Recurring: "recurring",
  Free: "free",
} as const;

export const ProductType: Record<
  Database["public"]["Enums"]["ProductType"],
  Database["public"]["Enums"]["ProductType"]
> = {
  PERSONAL_CLIENT_SERVICE: "PERSONAL_CLIENT_SERVICE",
  ADDON: "ADDON",
  AUTOMATED_CLIENT_SERVICE: "AUTOMATED_CLIENT_SERVICE",
  ONLY_CONTENT: "ONLY_CONTENT",
  EVENT: "EVENT",
} as const;

/**
 * Where and when a live event (EVENT product) takes place. Seat limits are not kept here -
 * they live on the price (`config.quantityLimit`) like for every other product.
 */
export interface EventDetails {
  /** ISO datetime. */
  startsAt: string;
  /** ISO datetime; null for a single-day event without a fixed end. */
  endsAt?: string | null;
  venueName: string;
  venueAddress?: string | null;
  city?: string | null;
}

export interface UpsellTo {
  productId: string;
  productName: string;
  checkoutId: string | null;
  presaleUrl?: string;
  linkLabel?: string;
}

export interface CrossSell {
  productId: string;
  productName: string;
  label?: string;
  /**
   * Pins the add-on offer to ONE price (TRE-1517). Without it the /addons
   * endpoint returns every visible price and the mobile app renders prices[0]
   * — i.e. whatever happens to be first in the product's prices array. An
   * explicitly pinned price is honoured even when `hidden` (hidden only means
   * "not on the public product page"), so promo prices can be add-on-only.
   */
  priceId?: string | null;
}

/**
 * The price a checkout lists for an order bump, the only one create-order accepts for it: the
 * pinned one (shown even when hidden), else the first visible one-time price.
 */
export const listedCrossSellPrice = <Price extends Pick<ProductPrice, "id" | "active" | "hidden" | "type">>(
  prices: readonly Price[] | null | undefined,
  pinnedPriceId?: string | null,
): Price | undefined =>
  pinnedPriceId
    ? prices?.find((price) => price.id === pinnedPriceId && price.active && price.type === "one_time")
    : prices?.find((price) => price.active && !price.hidden && price.type === "one_time");

export enum ProductDurationType {
  Infinite = "infinite",
  UntilDate = "untilDate",
  LastsFor = "lastsFor",
}

export interface UntilDateProductDuration {
  type: ProductDurationType.UntilDate;
  date: string;
}

export interface InfiniteProductDuration {
  type: ProductDurationType.Infinite;
}

export interface LastsForProductDuration {
  type: ProductDurationType.LastsFor;
  count: number;
  unit: "month" | "week";
}

export enum EndAccessStrategy {
  Cancel = "cancel",
  OfferWithTheSamePrice = "offerWithTheSamePrice",
  OfferWithTheCurrentPrices = "offerWithTheCurrentPrices",
}

export interface EndAccessOffers {
  productId: string;
  productName: string;
}

export interface ProductAuthor {
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string | null;
  creator?: string;
}

export interface BaseProduct extends Omit<
  ProductEntity,
  | "productDuration"
  | "upsellTo"
  | "prices"
  | "customInvoicesClient"
  | "customAuthor"
  | "endAccessOffers"
  | "crossSell"
  | "connectedAddons"
  | "eventDetails"
> {
  id: string;
  name: string;
  description: string;
  images: string[];
  metadata: Record<string, string>;
  status: ProductStatusEnum;
  createdAt: string;
  customAuthor: ProductAuthor | null;
  updatedAt: string;
  chargeType: ProductChargeTypeEnum;
  type: ProductTypeEnum;
  upsellTo?: UpsellTo[];
  crossSell?: {
    products: CrossSell[];
  };
  /**
   * Add-ons purchasable by clients of this product (TRE-1517) — rendered as
   * the "Rozszerz pakiet" section in the mobile app; checkout on the web.
   */
  connectedAddons?: {
    products: CrossSell[];
  } | null;
  endAccessOffers?: EndAccessOffers[];
  /** Set only on EVENT products. */
  eventDetails?: EventDetails | null;
  productDuration: null | UntilDateProductDuration | InfiniteProductDuration | LastsForProductDuration;
  owner: string;
  slug: string;
  enabledPaymentGateways: PaymentClientEnum[] | null;
  paymentsMethods: {
    blik?: false | "STRIPE" | "TPAY";
    p24?: false | "STRIPE" | null;
    applePay?: false | "STRIPE" | null;
    googlePay?: false | "STRIPE" | null;
    klarna?: false | "STRIPE" | null;
    card?: false | "STRIPE" | null;
  } | null;
}

export enum InvoicesClient {
  Fakturownia = "fakturownia",
  iFirma = "ifirma",
  wFirma = "wfirma",
  inFakt = "inFakt",
}

export type CustomInvoicesClient = {
  accountUrl: string;
  accountName: string;
  client: InvoicesClient;
};

export type ProductSubscriptionInterval = "day" | "month" | "week" | "year";

export type RecurringAccessPeriod = {
  type: "recurring";
  interval_count: number;
  interval: ProductSubscriptionInterval;
};

export type OneTimeAccessPeriod = {
  type: "one_time";
  interval_count: number;
  interval: ProductSubscriptionInterval;
  endAccessStrategy?: EndAccessStrategy;
};

export type PriceAccessPeriod = RecurringAccessPeriod | OneTimeAccessPeriod;

export type Currency = "pln" | "usd" | "eur" | "gbp";

export enum PriceStatus {
  Available = "available",
  Archived = "archived",
}
export interface BaseProductPrice {
  active: boolean;
  currency: Currency;
  livemode: boolean;
  createdAt: number;
  metadata?: Record<string, string>;
  id: string;
  type: PricePaymentType;
  unit_amount: number | null;
  unit_amount_decimal: string | null;
  clients: number;
  status: PriceStatus;
  paymentClient: PaymentClientEnum;
  accessPeriod: PriceAccessPeriod | null;
  customLabel?: string;
  config?: PriceConfig;
  checkoutId?: string | null;
  pendingOrders?: string[];
  hidden?: boolean;
  customInvoiceName?: string;
  installments?: number | null;
  connectedUpsells?: string[] | null;
  customDuration?: {
    count: number;
    unit: RecurringInterval;
  } | null;
}

export interface SuccessPageUpsellBenefit {
  title: string;
  description?: string | null;
  icon?: string | null;
}

/** One-click post-purchase offer shown on the checkout success page after buying this price. */
export interface SuccessPageUpsellConfig {
  enabled: boolean;
  /** Must be a different product on the same Stripe account as the purchased one. */
  targetProductId: string;
  targetPriceId: string;
  headline?: string | null;
  description?: string | null;
  benefitsTitle?: string | null;
  benefits?: SuccessPageUpsellBenefit[];
  /** In cents, like showAsPromo.priceBeforeDiscount; rendered strikethrough next to the target price. */
  priceBeforeDiscount?: number | null;
  badgeLabel?: string | null;
  /** Urgency countdown in minutes from first render; null = no timer. */
  timerMinutes?: number | null;
  acceptCtaLabel?: string | null;
  declineCtaLabel?: string | null;
}

export interface PriceConfig {
  additionalInfo?: string | null;
  quantityLimit?: number | null;
  showAvailable?: boolean;
  showAsPromo?:
    | false
    | {
        priceBeforeDiscount: number;
        promoTagText?: string | null;
        promoTagColor?: RadixColor | null;
      };
  activeUntil?: null | number;
  onlyForRenewal?: boolean;
  forAccessProlongationNotRequirePayment?: boolean;
  allowManualPayment?: boolean;
  allowOverlapRenewalWithCurrentSubscription?: boolean;
  showAsVipPrice?: boolean;
  /**
   * Installment payoff upsell (TRE-1511/TRE-1514): clients paying in Stripe
   * installments can pay off the remaining balance with a discount within
   * `windowDays` from the purchase date.
   */
  payoffUpsell?: {
    enabled: boolean;
    /** Days from product_client.createdAt during which the payoff offer is available. */
    windowDays: number;
    /** Discount applied to the remaining amount, percentage points (e.g. 10 = 10%). */
    discountPct: number;
  } | null;
  extra?: {
    notice?: string | null;
    benefits?: {
      title: string;
      description?: string | null;
      icon?: string | null;
    }[];
    benefitsTitle?: string | null;
    isRecommended?: boolean | null;
    recommendedLabel?: string | null;
    /** Short label rendered as a corner badge on the price card (e.g. "⭐️ NAJCZĘŚCIEJ WYBIERANA"). */
    cornerBadgeLabel?: string | null;
    ctaLabel?: string | null;
    selectedCtaLabel?: string | null;
  } | null;
  successPageUpsell?: SuccessPageUpsellConfig | null;
}

export interface StripePrice extends BaseProductPrice {
  paymentClient: "STRIPE";
  stripePrice: Stripe.Price;
  paymentsMethods?: {
    blik?: boolean;
    p24?: boolean;
    klarna?: boolean;
  };
}

export type ProductPrice = StripePrice;

export interface ServiceProduct extends BaseProduct {
  prices: ProductPrice[];
  productChargeType: ProductChargeTypeEnum;
  type: ProductTypeEnum;
  customInvoicesClient?: CustomInvoicesClient | null;
  productDuration: LastsForProductDuration | InfiniteProductDuration | UntilDateProductDuration;
}

export interface AddonProduct extends BaseProduct {
  prices: ProductPrice[];
  productChargeType: typeof ProductChargeType.PAID;
  productType: typeof ProductType.ADDON;
  totalClients: number;
  activeClients: number;
  churnedClients: number;
  totalFreeAccounts: number;
  customInvoicesClient?: CustomInvoicesClient | null;
}

export type RecurringInterval = "day" | "week" | "month" | "year";

export interface RecurringData {
  interval: RecurringInterval;
  interval_count: number;
}

export interface RecurringPrice extends RecurringData {
  currency: Currency;
  amount: number;
  type: typeof PaymentTypeOption.Recurring;
  active: boolean;
  id: string;
  paymentClient: PaymentClientEnum;
  connectedUpsells?: string[] | null;
  installments?: number | null;
  customDuration?: {
    count: number;
    unit: RecurringInterval;
  } | null;
}

export interface OneTimePrice {
  currency: Currency;
  amount: number;
  type: typeof PaymentTypeOption.OneTime;
  active: boolean;
  id: string;
  interval?: RecurringInterval;
  interval_count?: number;
  paymentClient: PaymentClientEnum;
  endAccessStrategy: EndAccessStrategy;
  installments?: number | null;
  connectedUpsells?: string[] | null;
  customDuration?: never;
}

/** Prices supplied when creating a product; gateway price ids are assigned after creation. */
export type CreateProductPriceInput = Omit<OneTimePrice, "id"> | Omit<RecurringPrice, "id">;

export interface CreateProductDTO {
  name: string;
  active: boolean;
  chargeType: ProductChargeTypeEnum;
  type: ProductTypeEnum;
  description: string;
  images: string[];
  metadata?: Record<string, string>;
  customInvoicesClient?: CustomInvoicesClient | null;
  upsellTo?: UpsellTo[];
  endAccessOffers?: EndAccessOffers[];
  productDuration: BaseProduct["productDuration"];
  prices: CreateProductPriceInput[];
  author?: ProductAuthor | null;
  crossSell?: {
    products: CrossSell[];
  };
  connectedAddons?: {
    products: CrossSell[];
  } | null;
  tpayIntegration?: string | null;
  customStripeAccount?: string | null;
  eventDetails?: EventDetails | null;
}

export interface FreeServiceProduct extends BaseProduct {
  prices: never[];
  chargeType: typeof ProductChargeType.FREE;
  type: typeof ProductType.AUTOMATED_CLIENT_SERVICE;
  customInvoicesClient?: CustomInvoicesClient | null;
  paymentsMethods: null;
}

export interface ContentProduct extends BaseProduct {
  prices: ProductPrice[];
  customInvoicesClient?: CustomInvoicesClient | null;
  chargeType: ProductChargeTypeEnum;
  type: typeof ProductType.ONLY_CONTENT;
  productDuration: null;
}

/**
 * Paid participation in a live meeting. Grants no app access and has no access duration.
 * `eventDetails` stays optional (as on BaseProduct): kysely inserts need the column to have the
 * same optionality across the whole Product union. The create/update validators require it.
 */
export interface EventProduct extends BaseProduct {
  prices: ProductPrice[];
  customInvoicesClient?: CustomInvoicesClient | null;
  chargeType: ProductChargeTypeEnum;
  type: typeof ProductType.EVENT;
  productDuration: null;
}

export type Product = ServiceProduct | ContentProduct | AddonProduct | FreeServiceProduct | EventProduct;

export interface UpdateProductDTO {
  name: string;
  status: ProductStatusEnum;
  description: string;
  images: string[];
  id: string;
  customInvoicesClient?: CustomInvoicesClient | null;
  upsellTo?: UpsellTo[];
  crossSell?: {
    products: CrossSell[];
  };
  connectedAddons?: {
    products: CrossSell[];
  } | null;
  author?: ProductAuthor;
  endAccessOffers?: EndAccessOffers[];
  eventDetails?: EventDetails | null;
}

export enum ProductPriceAction {
  Create = "create",
  Remove = "remove",
  Archive = "archive",
  ToggleStatus = "toggle_status",
  ToggleVisibility = "toggle_visibility",
  SetUpsell = "set_upsell",
}

export interface BaseProductPriceDTO {
  action: ProductPriceAction;
  productId: string;
}

export interface CreateProductPriceDTO extends BaseProductPriceDTO {
  action: ProductPriceAction.Create;
  price: OneTimePrice | RecurringPrice;
  /**
   * Duplicate: carry the presentation config (promo, limits, rich card, upsells, labels) over
   * from an existing price of the same product. The gateway price is still created fresh, so
   * only the parts that live in our own record are copied.
   */
  copyConfigFromPriceId?: string;
}

export interface RemoveProductPriceDTO extends BaseProductPriceDTO {
  action: ProductPriceAction.Remove;
  priceId: string;
}

export interface ArchiveProductPriceDTO extends BaseProductPriceDTO {
  action: ProductPriceAction.Archive;
  priceId: string;
}

export interface ToggleProductPriceStatusDTO extends BaseProductPriceDTO {
  action: ProductPriceAction.ToggleStatus;
  priceId: string;
  active: boolean;
}

export interface ToggleProductPriceVisibilityDTO extends BaseProductPriceDTO {
  action: ProductPriceAction.ToggleVisibility;
  priceId: string;
  hidden: boolean;
  checkoutId: string;
}

export interface SetProductPriceUpsellDTO extends BaseProductPriceDTO {
  action: ProductPriceAction.SetUpsell;
  priceId: string;
  connectedUpsells: string[] | null;
}

export interface CrossSellProductInfo {
  productId: string;
  productName: string;
  productImage: string | null;
  productDescription: string | null;
  priceId: string;
  priceAmount: number;
  priceCurrency: Currency;
  priceType: PricePaymentType;
  priceCustomLabel?: string;
  priceConfig?: PriceConfig | null;
  customInvoiceName?: string | null;
  priceDescription?: string | null;
}

export type UpdateProductPriceDTO =
  | CreateProductPriceDTO
  | RemoveProductPriceDTO
  | ToggleProductPriceStatusDTO
  | ArchiveProductPriceDTO
  | ToggleProductPriceVisibilityDTO
  | SetProductPriceUpsellDTO;

export interface StandardPriceInfo {
  amount: number;
  currency: Currency;
  active: true;
  id: string;
  accessPeriod: PriceAccessPeriod | null;
  customLabel?: string;
  config?: PriceConfig | null;
  available?: number | true;
  type: "recurring" | "one_time";
  priceId: string;
  allowedPaymentMethods?: (
    | "card"
    | "blik"
    | "p24"
    | "klarna"
    | "tpay"
    | "googlePay"
    | "applePay"
    | "manualPayment"
    | "tpay_paypo"
    | "tpay_paypal"
    | "tpay_raty"
  )[];
  connectedUpsells?: string[] | null;
  installments?: number | null;
  onlyForRenewal: boolean;
  code?: string | null;
  initialPeriod?: null | {
    amount: number;
    endDate: number;
    email: string;
    originalAmount: number;
  };
  customDuration?: {
    count: number;
    unit: RecurringInterval;
  } | null;
  extra?: PriceConfig["extra"] | null;
  /** Set when price config has showAsVipPrice; used to show VIP pricing in UI */
  vipPrice?: boolean;
}

export interface PaidProductInfo {
  active: boolean;
  isFree: false;
  images: string[];
  name: string;
  description: string;
  owner: string;
  author: ProductAuthor | null;
  price: StandardPriceInfo | null;
  prices: StandardPriceInfo[];
  upsellPrices: StandardPriceInfo[];
  cart?: CheckoutSettings["cart"];
  invoiceAsOption: boolean;
  stripeAccountId: string;
  allowUpgradeSubscriptionPlan?: boolean;
  allowDowngradeSubscriptionPlan?: boolean;
  hideCountryField?: boolean;
  allowPromotionCode?: false | "onlyForSubscription" | "forAll";
  showPhoneField?: false | "optional" | "required";
  showAdditionalInfo?: false | "optional" | "required";
  showLastNameField?: LastNameFieldValues;
  isMetaConversionAvailable: boolean;
  requireFullClientData?: boolean;
  renewalCartFallbackPath?: string | null;
  crossSell?: CrossSellProductInfo[] | null;
}

export interface FreeProductInfo {
  active: boolean;
  isFree: true;
  images: string[];
  name: string;
  description: string;
  owner: string;
  author: ProductAuthor | null;
  price: null;
  cart?: CheckoutSettings["cart"];
  customDomain?: string;
  renewalCartFallbackPath?: string | null;
}

export interface RenewalProductInfo {
  strategy: EndAccessStrategy;
  active: boolean;
  isFree: false;
  images: string[];
  name: string;
  description: string;
  owner: string;
  author: ProductAuthor | null;
  cart?: CheckoutSettings["cart"];
  currentPeriodEnd: number;
  client: ClientInvoiceData;
  price: StandardPriceInfo | null;
  prices: StandardPriceInfo[];
  upsellPrices: StandardPriceInfo[];
  invoiceAsOption: boolean;
  stripeAccountId: string;
  hideCountryField?: boolean;
  allowPromotionCode?: false | "onlyForSubscription" | "forAll";
  showPhoneField?: false | "optional" | "required";
  showAdditionalInfo?: false | "optional" | "required";
  showLastNameField?: LastNameFieldValues;
  isMetaConversionAvailable: boolean;
  installments?: number | null;
  currentCycle?: number | null;
  requireFullClientData?: boolean;
  renewalCartFallbackPath?: string | null;
  crossSell?: CrossSellProductInfo[] | null;
}

export type ProductInfoResponse = FreeProductInfo | PaidProductInfo | RenewalProductInfo;

export type ProductOrderError = {
  error?: string;
  message: string;
};

export type ProductOrderCreatedResponse =
  | {
      error?: never;
      message?: never;
      paymentIntentId: string;
      successUrl: string;
      cancelUrl: string;
      subscription?: never;
      paymentIntent?: never;
      orderId: string;
      clientSecret: string;
      /** One-time sign-in URL to the client web app, minted only for freshly created trial accounts. */
      webSignInUrl?: string;
    }
  | {
      error?: never;
      message?: never;
      setupIntentId: string;
      successUrl: string;
      cancelUrl: string;
      subscription?: never;
      paymentIntent?: never;
      orderId: string;
      clientSecret: string;
      paymentIntentId?: never;
    }
  | {
      error?: never;
      message?: never;
      paymentIntentId: string | null;
      successUrl: string;
      cancelUrl: string;
      subscription: Stripe.Subscription;
      paymentIntent: Stripe.PaymentIntent | null;
      orderId: string;
      clientSecret?: never;
    }
  | {
      error?: never;
      message?: never;
      redirectUrl: string;
      orderId: string;
      paymentClient: typeof PaymentClient.TPAY;
    }
  | {
      error?: never;
      message?: never;
      subscription?: never;
      paymentIntentId?: never;
      paymentClient: typeof PaymentClient.TPAY;
      orderId: string;
      successUrl: string;
      cancelUrl: string;
      transactionId: string;
      fallbackUrl: string;
    }
  | {
      error?: never;
      message?: never;
      redirectUrl: string;
      orderId: string;
      paymentClient: typeof PaymentClient.MANUAL;
    };
